import React from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Card, Steps } from 'antd';
import { differenceInSeconds } from 'date-fns';
import styled from 'styled-components';

import type { IOtaStep } from '../api/getActiveOta';
import { OTA_STEP_LABEL, STEP_STATUS_MAP } from '../helpers/ota';
import { useBoxOtaActiveProgressView } from '../hooks/useBoxOtaActiveProgressView';
import { OTA_STEP_STATUS } from '../types';
import { formatDuration, formatISODate } from '@/helpers/dates';
import { ElapsedTimer } from '@/shared/components/ElapsedTimer';

const CompactSteps = styled(Steps)`
  .ant-steps-item {
    flex: none !important;
    margin-right: 1.5rem;
  }
`;

interface IBoxOtaActiveProgressProps {
  lpUI: string;
}

const getCompletedDuration = (step: IOtaStep): number | undefined => {
  if (
    step.status !== OTA_STEP_STATUS.COMPLETED ||
    !step.started_at ||
    !step.completed_at
  ) {
    return undefined;
  }

  return differenceInSeconds(
    new Date(step.completed_at),
    new Date(step.started_at)
  );
};

const buildStepDescription = (
  step: IOtaStep,
  isActiveStep: boolean
): React.ReactNode => {
  if (isActiveStep && step.started_at) {
    return <ElapsedTimer startedAt={step.started_at} />;
  }

  const completedDuration = getCompletedDuration(step);

  if (completedDuration !== undefined) {
    return formatDuration(completedDuration);
  }

  return undefined;
};

export const BoxOtaActiveProgress = ({ lpUI }: IBoxOtaActiveProgressProps) => {
  const { isInProgress, steps, firstPendingIndex, executionStartedAt } =
    useBoxOtaActiveProgressView(lpUI);

  if (!isInProgress) return null;

  return (
    <Card
      size="small"
      title={
        executionStartedAt
          ? `OTA in progress - Started at: ${formatISODate(executionStartedAt)}`
          : 'OTA in progress'
      }
    >
      <CompactSteps
        size="small"
        items={steps.map((step, index) => {
          const isActiveStep =
            step.status === OTA_STEP_STATUS.IN_PROGRESS ||
            index === firstPendingIndex;

          return {
            key: step.step,
            title: OTA_STEP_LABEL[step.step] ?? step.step,
            status: STEP_STATUS_MAP[step.status] ?? 'wait',
            icon: isActiveStep ? <LoadingOutlined /> : undefined,
            description: buildStepDescription(step, isActiveStep),
          };
        })}
      />
    </Card>
  );
};
